// src/services/messageOrder.js — one order, agreed with the phone.
//
// ══ WHAT THIS USED TO DO, AND WHY IT STOPPED ═══════════════════════════════
// It ordered by the signaling server's per-chat `seq`, with seqless rows
// slotted between sequenced anchors. The reasoning was sound as far as it went:
// two phones' clocks disagree, `seq` is one number both platforms can read, so
// let the server decide. The app did the same thing.
//
// 🟥 AND IT PRODUCED THE TWO BUGS THAT WERE REPORTED, which are one bug seen
//    from two sides:
//
//    1. "B sends at the same moment I send. B's message shows UNDER mine, then
//        changes places and goes ABOVE it." A message reaches a client twice —
//        first without a seq, then with one — so the row was placed by one key
//        and then re-placed by another. Nothing was wrong with either key. The
//        bug was HAVING TWO.
//
//    2. "B sent first but my connection was weak. When it finally arrives it
//        appears ON TOP of the message I had already sent." `seq` is the
//        SERVER's order and the server saw B first, so honouring it means a
//        message may insert itself above something the reader watched
//        themselves send. The conversation rewrites its own history.
//
// ══ THE KEY NOW: ARRIVAL ═══════════════════════════════════════════════════
// Every row is keyed to THIS client's clock — my own sends to my send time,
// incoming messages to when I received them. One clock, so there is no skew to
// compare; append-only, so nothing already on screen can move. A seq ack is
// then just metadata: receipts and dedupe still use it, ordering never does.
// Mirrors stores/messageOrder.ts on the phone, which carries the long form of
// this reasoning.
//
// 🟥 AND THE PRICE, WHICH IS PAID OUT LOUD. Arrival order can seat a slow
//    message BELOW a reply that was sent after it, so the clock under the
//    bubbles reads backwards. That is bug 2 demoted from a correctness problem
//    to a cosmetic one — a strictly better trade, but only if the odd-looking
//    bubble says why. `showsLateSendTime` marks that one bubble, and only that
//    one, and the renderer prints its send time with the word "sent".

const tsMs = (t) => {
  if (!t) return 0;
  if (typeof t === 'number') return t;
  if (typeof t.toMillis === 'function') return t.toMillis();
  if (typeof t.seconds === 'number') return t.seconds * 1000;
  const d = new Date(t).getTime();
  return Number.isNaN(d) ? 0 : d;
};

/* Older than this and a message is not arriving, it is being READ BACK.
   🟥 THIS IS THE TRAP THAT KILLED ARRIVAL ORDER THE FIRST TIME IT WAS TRIED.
      Stamping "now" is right for a message arriving; it is catastrophic for a
      batch being LOADED — a chat opened after the fact gives every incoming row
      the key "now" while my own rows keep their real send times, which sinks my
      entire half of the conversation below theirs. History keys to its own send
      time, which is also what keeps a cold load correctly interleaved. */
export const LIVE_ARRIVAL_MAX_MS = 2 * 60 * 1000;
/* The floor that stops ordinary clock jitter between two devices from being
   announced as an anomaly. Not a "how late is late" threshold — see
   showsLateSendTime for why lateness is the wrong question. */
export const SEND_ORDER_SKEW_MS = 2 * 1000;

let _selfUid = null;
export const setSelfUid = (uid) => { if (uid) _selfUid = uid; };

let _lastOrd = 0;
/* 🟥 Date.now() STANDS STILL FOR WHOLE MILLISECONDS, so two messages in one
   snapshot would share a rank and the tie-break would decide the order instead
   of arrival. Monotonic by construction. */
const nextOrd = () => { const t = Date.now(); _lastOrd = _lastOrd >= t ? _lastOrd + 1 : t; return _lastOrd; };

/* ── the registry ──────────────────────────────────────────────────────────
   🟥 THE PHONE CAN KEEP `_ord` ON THE MESSAGE; THIS CLIENT CANNOT. Every
      Firestore snapshot rebuilds its rows as brand-new objects, so a key written
      onto a row is gone on the next update — and a key that is re-derived is not
      an arrival key at all, it is just "now" again, which would re-order the
      list on every snapshot. So the key lives in a registry owned by the id.

   🟢 AND IT SURVIVES A RELOAD, for the same reason: a reload inside the live
      window would otherwise re-stamp the last two minutes of the chat in
      timestamp order and undo exactly the fix this file exists for. Bounded, and
      every access is guarded — a browser with storage disabled gets correct
      ordering for this page's lifetime, which is the old behaviour, not a crash.
   ────────────────────────────────────────────────────────────────────────── */
const ORD_KEY = 'allo_msg_ord_v1';
const ORD_MAX = 4000;
let _ord = null;

const loadOrds = () => {
  if (_ord) return _ord;
  _ord = new Map();
  try {
    const raw = localStorage.getItem(ORD_KEY);
    if (raw) for (const [k, v] of JSON.parse(raw)) _ord.set(String(k), Number(v));
  } catch { /* unreadable or disabled — an empty registry is still correct */ }
  return _ord;
};

let _saveTimer = null;
const saveOrds = () => {
  if (_saveTimer) return;                       // one write per tick, not one per message
  _saveTimer = setTimeout(() => {
    _saveTimer = null;
    try {
      const m = loadOrds();
      /* Map keeps insertion order, so the oldest entries are the ones to drop. */
      const entries = [...m.entries()];
      const keep = entries.length > ORD_MAX ? entries.slice(entries.length - ORD_MAX) : entries;
      if (keep.length !== entries.length) { _ord = new Map(keep); }
      localStorage.setItem(ORD_KEY, JSON.stringify(keep));
    } catch { /* full or disabled — in-memory ordering is unaffected */ }
  }, 400);
};

/**
 * Give every row its arrival key, once, and never a second time.
 *
 * Mine keys to MY send time — so a message I just sent cannot be pushed down by
 * an incoming one that is stamped while my own send is still resolving, and an
 * unresolved serverTimestamp (which reads 0) cannot sink it off the top.
 * Theirs keys to the moment I received it, except for history (see
 * LIVE_ARRIVAL_MAX_MS).
 */
export function stampArrival(list) {
  if (!Array.isArray(list) || !list.length) return;
  const reg = loadOrds();
  const fresh = [];
  let maxOrd = 0;
  for (const m of list) {
    if (!m || !m.id) continue;
    /* ⏱ 🧵 THE SERVER'S CLOCK FIRST (`sat`, given with the message's number and carried by every copy):
       the same key on every member's phone and browser, so the same order everywhere — "make sure all
       messages, even system messages, have the same order for every member". Mirrors the phone's
       stores/messageOrder. */
    const sat = Number(m.sat);
    if (m.sat != null && Number.isFinite(sat) && sat > 0) {
      m._ord = sat;
      if (reg.get(String(m.id)) !== sat) reg.set(String(m.id), sat);
      if (sat > maxOrd) maxOrd = sat;
      continue;
    }
    const had = reg.get(String(m.id));
    if (had != null) { m._ord = had; if (had > maxOrd) maxOrd = had; continue; }
    fresh.push(m);
  }
  if (!fresh.length) { saveOrds(); return; }
  /* 🟢 A BATCH IS STAMPED IN SEND ORDER, NOT IN THE ORDER THE SNAPSHOT LISTED IT.
     A reconnect flushes a backlog in one go; within that batch the only honest
     order is the one the messages were sent in. */
  fresh.sort((a, b) => tsMs(a.timestamp) - tsMs(b.timestamp));
  let myFloor = 0;
  const now = Date.now();
  for (const m of fresh) {
    const t = tsMs(m.timestamp);
    let v;
    const history = t > 0 && (now - t) > LIVE_ARRIVAL_MAX_MS;
    if (_selfUid && m.senderId === _selfUid) {
      v = t > 0 ? t : nextOrd();
      /* ⏱ a send of mine still waiting for the server's clock is the newest thing here */
      if (!history && v <= maxOrd) v = maxOrd + 1;
      if (v <= myFloor) v = myFloor + 1;        // my own burst stays strictly increasing
      myFloor = v;
    } else {
      /* ⏱ arriving without the server's clock: the newest end, never above a row already placed */
      v = history ? t : Math.max(nextOrd(), maxOrd + 1);
    }
    if (v > maxOrd) maxOrd = v;
    m._ord = v;
    reg.set(String(m.id), v);
  }
  saveOrds();
}

const ordOf = (m) => (m && m._ord != null ? Number(m._ord) : tsMs(m && m.timestamp));

/**
 * Oldest → newest, by arrival.
 *
 * `pendingMs` is kept for callers: a message this client just wrote reads
 * `timestamp: null` until the server resolves it, and 0 would sort it to the
 * very top. Its arrival key covers that too, so it is only the fallback for a
 * row that somehow reaches here unstamped.
 */
export function orderMessages(list, pendingMs = Date.now()) {
  if (!Array.isArray(list) || list.length < 2) return list || [];
  stampArrival(list);
  return list
    .map((m, i) => ({ m, i, k: ordOf(m) || pendingMs }))
    .sort((a, b) => (a.k - b.k)
      /* 🟥 A TIE MUST NOT BE BROKEN BY THE INPUT ORDER — that is the whole
         disease. The id is intrinsic and identical on every device, so the phone
         breaks the tie the same way. */
      || (String(a.m.id || '') < String(b.m.id || '') ? -1
        : String(a.m.id || '') > String(b.m.id || '') ? 1 : a.i - b.i))
    .map((x) => x.m);
}

/** How long after it was sent this client saw it. Zero for my own by construction. */
export function arrivedLateBy(m) {
  if (!m || (_selfUid && m.senderId === _selfUid)) return 0;
  if (m.sat != null && Number(m.sat) > 0) return 0;   // ⏱ placed by the server's clock: never late
  const ord = m._ord;
  const sent = tsMs(m.timestamp);
  if (ord == null || !(sent > 0)) return 0;
  const d = Number(ord) - sent;
  return d > 0 ? d : 0;
}

/**
 * Does this row sit below a message that was sent AFTER it?
 *
 * `above` is the row drawn directly above `curr`. This list is oldest → newest,
 * so that is the PRECEDING entry — the opposite of the phone, whose list is
 * newest-first and inverted. Same question, mirrored index.
 */
export function sendOrderInverted(curr, above) {
  if (!curr || !above) return false;
  const c = tsMs(curr.timestamp);
  const a = tsMs(above.timestamp);
  if (!(c > 0) || !(a > 0)) return false;
  return (a - c) >= SEND_ORDER_SKEW_MS;
}

/**
 * Should this bubble print the time it was SENT rather than a bare clock time?
 *
 * 🟥 THE LATENESS CONDITION IS NOT DECORATION. Without it a COLD LOAD can label
 *    history: there every row's `_ord` IS its own old timestamp, so two devices
 *    whose clocks disagree by more than the skew would be positioned by one of
 *    those clocks and then accused by the other, on every single open.
 */
export function showsLateSendTime(curr, above) {
  return sendOrderInverted(curr, above) && arrivedLateBy(curr) > 0;
}
